"use client";

/**
 * Unified /book page — Phase 2 of the booking consolidation.
 *
 * One 5-step flow that absorbs the old /consultation scheduler +
 * deposit + photo upload into the same page that picks services.
 * One server POST creates BookingGroup + Appointment + ServicePickup
 * (and charges Square deposit when required) atomically.
 *
 * Steps:
 *   1. service     — wantsNewWigConsult checkbox + <WigList>
 *   2. schedule    — only if needsAppointment, <DatetimeStep>
 *   3. dropoff     — only if needsPickup, <DropoffSection>
 *   4. details     — <ContactStep> + notes + <UploadSection>
 *   5. confirm     — summary + <DepositCard> when deposit-bearing
 *
 * Steps 2 and 3 conditionally render based on the customer's
 * selections — drop-off-only customers skip schedule, appointment-
 * only customers skip dropoff, and the most common combo (one
 * appointment + one drop-off) sees all five.
 *
 * Gated by `siteConfig.unifiedBookingFlow`. The chooser at
 * src/app/[locale]/book/page.tsx mounts this when the flag is on
 * and <LegacyBookPage> when it's off.
 *
 * See wiki/plans/booking-consolidation-2026-04-28.md.
 */

import { useEffect, useMemo, useRef, useState } from "react";
import Image from "next/image";
import { useSession } from "next-auth/react";
import { useSearchParams } from "next/navigation";
import { useTranslations } from "next-intl";
import { Link } from "@/i18n/routing";
import { ArrowRight, CheckCircle2, ChevronLeft, Loader2, Sparkles, Check, Crown, Plus } from "lucide-react";
import { useToast } from "@/context/ToastContext";
import { useSlotReservation } from "@/hooks/useSlotReservation";
import SlotReservationBanner from "@/components/booking/SlotReservationBanner";
import SlotReservationExpiredModal from "@/components/booking/SlotReservationExpiredModal";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import WigList from "@/components/booking/WigList";
import DropoffSection from "@/components/booking/DropoffSection";
import ContactStep from "@/components/booking/ContactStep";
import DatetimeStep from "@/components/booking/DatetimeStep";
import DepositCard from "@/components/booking/DepositCard";
import ServiceFeeCard, { type PricedService } from "@/components/booking/ServiceFeeCard";
import { useSiteConfig } from "@/context/SiteConfigContext";
import BookingSummary from "@/components/booking/BookingSummary";
import PlaceAutocomplete from "@/components/ui/PlaceAutocomplete";
import { emptyWig, SERVICES, type WigLine } from "@/lib/booking-services";
import { formatPrice } from "@/lib/utils";
import CourierTrackMap from "@/components/booking/CourierTrackMap";

type Step = "service" | "schedule" | "dropoff" | "details" | "confirm";

interface BookedResult {
  appointmentNumber: string | null;
  pickupNumber: string | null;
  appointmentStatus: string | null;
  /** ServicePickup id — needed to cancel the courier from the success card. */
  pickupId: string | null;
  /** True when the send-in Uber auto-dispatched (offer immediate cancel). */
  courierDispatched: boolean;
  /** Uber live-tracking URL for the dispatched courier, if any. */
  courierTrackingUrl: string | null;
  /** Human label ("Fri, Jul 4 at 2:30 PM") when the Uber was scheduled
   *  for later instead of dispatched now. */
  courierScheduledLabel: string | null;
}

export default function UnifiedBookPage() {
  const { toast } = useToast();
  const { data: session } = useSession();
  const t = useTranslations("booking");

  // ─── State ─────────────────────────────────────────────────────────
  // Pre-select from ?service=... query param (Manny: linked from
  // /wigs "Book a Consultation" button etc.). new_wig flips the
  // consultation toggle; any other id seeds the first wig's
  // selectedServices so the customer lands with a head start.
  const searchParams = useSearchParams();
  const prefilledService = searchParams.get("service") || "";
  // Intent mode (Rivky #151) — split the booking entry by what the
  // customer came here to do. "service" (default) shows the per-wig
  // service picker prominently with a small "+ Also need a new wig
  // consultation?" link at the bottom. "consultation" hides the
  // service list, shows the new-wig card prominently, with a small
  // "+ Also need servicing?" link to expand the service flow inline.
  // Backward compat: ?service=new_wig also routes into consultation
  // intent so the existing CTAs keep working.
  const intentParam = searchParams.get("intent") || "";
  const isConsultationIntent =
    intentParam === "consultation" || prefilledService === "new_wig";
  const [wantsNewWigConsult, setWantsNewWigConsult] = useState(isConsultationIntent);
  // Lets the customer add the OTHER mode's section inline via the
  // small cross-link at the bottom of the page.
  const [showServiceAddon, setShowServiceAddon] = useState(false);
  const [showConsultAddon, setShowConsultAddon] = useState(false);
  // Refs for the cross-link sections so clicking '+ Also need a new
  // wig consultation?' / '+ Also need servicing?' scrolls the newly-
  // revealed card into view AND auto-selects it, instead of silently
  // toggling visibility 700px above the customer's viewport (#159).
  const consultSectionRef = useRef<HTMLElement | null>(null);
  const wigListSectionRef = useRef<HTMLDivElement | null>(null);
  // Seeded with one empty wig so the service options are visible
  // without an extra "Add a wig" click.
  const [wigs, setWigs] = useState<WigLine[]>(() => {
    const seed = emptyWig(null);
    // Seed first wig with the prefilled service unless it's the
    // new-wig consultation (handled by the toggle above) or empty.
    if (prefilledService && prefilledService !== "new_wig") {
      const valid = SERVICES.find(
        (s) => s.id === prefilledService && !s.depositRedirect
      );
      if (valid) {
        seed.selectedServices = [prefilledService];
        // Only preselect when the service leaves no choice. A service
        // that supports both paths must be chosen by the customer —
        // /book?service=wash_set is the nav + homepage entry point, and
        // silently seeding "appointment" would push the weekly drop-off
        // regulars into the calendar without them ever seeing that
        // dropping off was an option.
        seed.location =
          valid.allowedLocations === "both" ? null : valid.allowedLocations[0];
      }
    }
    return [seed];
  });
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [smsOptIn, setSmsOptIn] = useState(true);
  const [notes, setNotes] = useState("");

  // Schedule state — owned here, passed into <DatetimeStep>
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  // Set when the picked time was taken before submit; DatetimeStep shows
  // it inline so the customer knows to choose another hour or day.
  const [slotTakenNotice, setSlotTakenNotice] = useState<string | null>(null);
  useEffect(() => {
    if (time) setSlotTakenNotice(null);
  }, [time]);

  // Dropoff state — owned here, passed into <DropoffSection>
  const [dropDate, setDropDate] = useState("");
  // Uber courier is now two INDEPENDENT legs (Rivky 2026-06-28):
  //   uberDropoff — send it IN with Uber (courier picks up from the
  //                 customer's address and brings it to the salon)
  //   uberReturn  — send it BACK with Uber once the work is done
  //                 ("minimum N days, or whenever it's ready")
  // Either leg needs the customer's address, so `wantsCourier` is a
  // derived OR used for the address form + save-default gating.
  const [uberDropoff, setUberDropoff] = useState(false);
  const [uberReturn, setUberReturn] = useState(false);
  // Uber timing — false = dispatch immediately at booking (default),
  // true = deferred dispatch at the customer's chosen ET date + time
  // (cron fires it, with a reminder text ~1h before).
  const [uberSchedule, setUberSchedule] = useState(false);
  const [uberScheduleDate, setUberScheduleDate] = useState("");
  const [uberScheduleTime, setUberScheduleTime] = useState("");
  const wantsCourier = uberDropoff || uberReturn;
  // Optional: customer wants to come IN for a styling appointment once
  // the wash & set is done. Timing depends on when the work finishes,
  // so we don't book a hard slot here — we flag the ticket and the
  // salon schedules styling when it's ready (surfaced in pickup notes).
  const [wantsStyling, setWantsStyling] = useState(false);

  // ─── Pickup address (when wantsCourier === true) ──────────
  // Auto-fill from User.addresses default on mount; PlaceAutocomplete
  // gives one-input UX with US-only Google suggestions. Falls back to
  // 4-field manual entry when Google is unreachable. The 400 from
  // /api/service-pickup ("Pickup address and phone are required for
  // courier service") happens because /book never collected this
  // before — addresses are now forwarded into the servicePickup
  // payload below.
  const [pickupStreet, setPickupStreet] = useState("");
  const [pickupCity, setPickupCity] = useState("");
  const [pickupState, setPickupState] = useState("");
  const [pickupZip, setPickupZip] = useState("");
  // Live Uber quote shown before the customer finalizes, once they've
  // entered a courier address (Rivky 2026-06-28). Fee is per direction.
  const [uberQuote, setUberQuote] = useState<{ fee: number; minutes: number } | null>(null);
  const [quoteLoading, setQuoteLoading] = useState(false);
  // Courier-cancel state for the post-booking success card.
  const [courierCancelState, setCourierCancelState] = useState<
    "idle" | "canceling" | "canceled" | "error"
  >("idle");
  const [savedPickupAddressId, setSavedPickupAddressId] = useState<string | null>(null);
  const [pickupAddressAutoFilled, setPickupAddressAutoFilled] = useState(false);
  // Default ON when there's nothing saved (first-time customer should
  // get their address kept). Flipped OFF below when /api/account
  // returns ≥ 1 saved Address — editing fields then doesn't silently
  // overwrite the canonical default.
  const [savePickupAsDefault, setSavePickupAsDefault] = useState(true);

  // Settings fetched once from /api/appointments/settings; both
  // <DatetimeStep>'s booking-window check and (later) the dropoff
  // step could read from it.
  const [bookingWindowDays, setBookingWindowDays] = useState(60);
  const [holidayExtensionDates, setHolidayExtensionDates] = useState<Set<string>>(
    () => new Set<string>()
  );
  // Weekly closures + per-day service restrictions. <DatetimeStep> greys
  // out the matching calendar days so a customer never picks a date the
  // slots API is guaranteed to answer "closed" for.
  const [closedDays, setClosedDays] = useState<number[]>([]);
  const [allowedTypesByDay, setAllowedTypesByDay] = useState<
    Record<string, string[]>
  >({});
  // Per-type drop-off lead time (Phase 4). When the customer picks a
  // type that requires advance drop-off, the schedule step shows a
  // yellow callout with the configured day count.
  const [dropoffDaysByType, setDropoffDaysByType] = useState<Record<string, number>>({});

  // Deposit state — fetched from /api/appointments/deposit-check
  // whenever the appointment type changes. Renders <DepositCard> on
  // the confirm step when amount > 0.
  const [depositAmount, setDepositAmount] = useState<number | null>(null);
  const [payingDeposit, setPayingDeposit] = useState(false);

  // Pay-at-booking state — derivation lives after appointmentType +
  // additionalTypes are declared (#148, see below).
  const [payingServiceFee, setPayingServiceFee] = useState(false);
  const { retailApptServices, retailColorServices, retailRepairServices } = useSiteConfig();

  // 10-min slot reservation. Same hook /consultation uses; the
  // reservation id flows server-side via /api/book → /api/appointments.
  const slotReservation = useSlotReservation();

  const [submitting, setSubmitting] = useState(false);
  const [currentStep, setCurrentStep] = useState<Step>("service");
  // After a successful submit we render an inline success card on this
  // page rather than redirecting to /account/* (those routes are
  // re-entrant booking forms — landing on them post-submit looks like
  // "the next booking page" and confused customers in early testing).
  const [booked, setBooked] = useState<BookedResult | null>(null);

  // ─── Derived values ────────────────────────────────────────────────
  const hasInPersonWig = wigs.some(
    (w) => w.location === "appointment" && w.selectedServices.length > 0
  );
  const hasDropOffWig = wigs.some(
    (w) => w.location === "dropoff" && w.selectedServices.length > 0
  );
  const needsAppointment = wantsNewWigConsult || hasInPersonWig;
  const needsPickup = hasDropOffWig;

  // Primary appointment type — new-wig if picked, else first
  // appointment-mode service across all wigs. The server uses this
  // to size the slot duration + apply the deposit lookup.
  const appointmentType = useMemo<string>(() => {
    if (wantsNewWigConsult) return "new_wig";
    const firstInPerson = wigs.find(
      (w) => w.location === "appointment" && w.selectedServices.length > 0
    );
    return firstInPerson?.selectedServices[0] ?? "";
  }, [wantsNewWigConsult, wigs]);

  // Additional services that ride along on the same appointment
  // (e.g. "wash + color" → primary=wash_set, additional=[color]).
  const additionalTypes = useMemo<string[]>(() => {
    const extras: string[] = [];
    for (const w of wigs) {
      if (w.location !== "appointment") continue;
      for (const s of w.selectedServices) {
        if (s !== appointmentType && !extras.includes(s)) extras.push(s);
      }
    }
    return extras;
  }, [wigs, appointmentType]);

  // Pay-at-booking for retail services with paymentMode != "free"
  // (Manny #148). Reads admin-managed retailApptServices from
  // SiteConfig and matches by SERVICES.label (case-insensitive).
  // Skipped when a deposit-bearing type (new-wig consult) is in
  // play — DepositCard handles that flow.
  const serviceFeeServices: PricedService[] = useMemo(() => {
    if (depositAmount && depositAmount > 0) return [];
    const priceMap = new Map<
      string,
      { price: number; priceMax?: number; mode?: string }
    >();
    for (const r of [
      ...(retailApptServices || []),
      ...(retailColorServices || []),
      ...(retailRepairServices || []),
    ]) {
      if (!r?.name) continue;
      priceMap.set(r.name.toLowerCase(), {
        price: r.price,
        priceMax: r.priceMax,
        mode: r.paymentMode,
      });
    }
    const selectedIds = new Set<string>();
    for (const w of wigs) {
      if (w.location !== "appointment" && w.location !== "dropoff") continue;
      for (const s of w.selectedServices) selectedIds.add(s);
    }
    if (appointmentType) selectedIds.add(appointmentType);
    for (const t of additionalTypes) selectedIds.add(t);

    const priced: PricedService[] = [];
    for (const id of selectedIds) {
      const def = SERVICES.find((s) => s.id === id);
      if (!def) continue;
      const entry = priceMap.get(def.label.toLowerCase());
      if (!entry || !entry.mode || entry.mode === "free") continue;
      // Charge the BASE price up front, not the top of the range. For a
      // service priced "$65–$200" we collect $65 at booking; the salon
      // adjusts the final amount in person (see the "final price may
      // vary" caveat). Charging priceMax overcharged the customer.
      const amount = entry.price;
      if (!amount || amount <= 0) continue;
      priced.push({
        id,
        amount,
        paymentMode: entry.mode as "request_with_skip" | "require",
        label: def.label,
      });
    }
    return priced;
  }, [
    depositAmount,
    retailApptServices,
    retailColorServices,
    retailRepairServices,
    wigs,
    appointmentType,
    additionalTypes,
  ]);

  // Steps adapt to selections. service + details + confirm are always
  // present; schedule + dropoff appear when relevant.
  const stepsList = useMemo<Step[]>(() => {
    const arr: Step[] = ["service"];
    if (needsAppointment) arr.push("schedule");
    if (needsPickup) arr.push("dropoff");
    arr.push("details", "confirm");
    return arr;
  }, [needsAppointment, needsPickup]);

  // Useful labels for header / display
  const selectedServiceData = useMemo(
    () => SERVICES.find((s) => s.id === appointmentType) || null,
    [appointmentType]
  );
  const dropoffWigCount = useMemo(
    () => wigs.filter((w) => w.location === "dropoff" && w.selectedServices.length > 0).length,
    [wigs]
  );

  // ─── Effects ───────────────────────────────────────────────────────

  // Prefill from session
  useEffect(() => {
    if (session?.user) {
      if (session.user.name && !name) setName(session.user.name);
      if (session.user.email && !email) setEmail(session.user.email);
      const userPhone = (session.user as { phone?: string }).phone;
      if (userPhone && !phone) setPhone(userPhone);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session]);

  // Fetch booking-window + holiday-extension config once on mount.
  // Failure falls back to the 60-day default + empty set.
  useEffect(() => {
    fetch("/api/appointments/settings")
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (data?.bookingWindowDays) {
          setBookingWindowDays(Number(data.bookingWindowDays));
        }
        if (Array.isArray(data?.holidayExtensionDates)) {
          setHolidayExtensionDates(new Set<string>(data.holidayExtensionDates));
        }
        if (Array.isArray(data?.closedDays)) {
          setClosedDays(data.closedDays.map(Number).filter(Number.isInteger));
        }
        if (
          data?.allowedTypesByDay &&
          typeof data.allowedTypesByDay === "object" &&
          !Array.isArray(data.allowedTypesByDay)
        ) {
          setAllowedTypesByDay(
            data.allowedTypesByDay as Record<string, string[]>
          );
        }
        if (
          data?.dropoffDaysByType &&
          typeof data.dropoffDaysByType === "object" &&
          !Array.isArray(data.dropoffDaysByType)
        ) {
          setDropoffDaysByType(
            data.dropoffDaysByType as Record<string, number>
          );
        }
      })
      .catch(() => {});
  }, []);

  // Pickup-address pre-fill from User.addresses default. Mirrors the
  // /account/service-pickup pattern so logged-in customers don't
  // retype their address every booking. Skipped for guest checkouts
  // (no session) — they fill manually.
  useEffect(() => {
    if (!session?.user?.id) return;
    fetch("/api/account")
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (!data?.addresses) return;
        const addrs: Array<{
          id: string;
          line1: string;
          city: string;
          state: string;
          zip: string;
          isDefault: boolean;
        }> = data.addresses;
        const def =
          addrs.find((a) => a.isDefault) || (addrs.length === 1 ? addrs[0] : null);
        if (def) {
          setSavedPickupAddressId(def.id);
          setPickupStreet(def.line1);
          setPickupCity(def.city);
          setPickupState(def.state);
          setPickupZip(def.zip);
          setPickupAddressAutoFilled(true);
        }
        // Saved Address(es) exist → flip save-as-default OFF so a one-
        // off booking from a different address doesn't overwrite
        // canonical default.
        if (addrs.length > 0) setSavePickupAsDefault(false);
      })
      .catch(() => {});
  }, [session?.user?.id]);

  // Resolve deposit amount whenever the appointment type changes.
  // /api/appointments/deposit-check returns 0 when none required or
  // when Platinum waives.
  useEffect(() => {
    if (!appointmentType) {
      setDepositAmount(null);
      return;
    }
    fetch("/api/appointments/deposit-check", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ type: appointmentType }),
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (data && typeof data.amountDollars === "number") {
          setDepositAmount(
            data.required && data.amountDollars > 0 ? data.amountDollars : 0
          );
        } else {
          setDepositAmount(0);
        }
      })
      .catch(() => setDepositAmount(0));
  }, [appointmentType]);

  // Live Uber quote — fetched (debounced) once the customer picks a
  // courier leg AND has a complete pickup address, so they see the real
  // fee before they finalize. Fee is per direction. The salon address
  // is the public store address (kept inline to avoid a server-const
  // import in this client bundle).
  useEffect(() => {
    const STORE = "1935 Whitesville Rd, Toms River, NJ 08755";
    const complete =
      !!pickupStreet.trim() && !!pickupCity.trim() && !!pickupState.trim() && !!pickupZip.trim();
    if (!wantsCourier || !complete) {
      setUberQuote(null);
      setQuoteLoading(false);
      return;
    }
    const customerAddress = `${pickupStreet}, ${pickupCity}, ${pickupState} ${pickupZip}`;
    let cancelled = false;
    setQuoteLoading(true);
    const t = setTimeout(() => {
      fetch("/api/delivery/quote", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ pickupAddress: customerAddress, dropoffAddress: STORE }),
      })
        .then((r) => (r.ok ? r.json() : null))
        .then((data) => {
          if (cancelled) return;
          if (data && typeof data.fee === "number" && data.available) {
            setUberQuote({ fee: data.fee, minutes: data.estimatedMinutes || 0 });
          } else {
            setUberQuote(null);
          }
        })
        .catch(() => {
          if (!cancelled) setUberQuote(null);
        })
        .finally(() => {
          if (!cancelled) setQuoteLoading(false);
        });
    }, 600);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [wantsCourier, pickupStreet, pickupCity, pickupState, pickupZip]);

  // ─── Validation ────────────────────────────────────────────────────
  // Sub-tier requirement (Rivky #151 follow-up): if a customer picks
  // a service with a populated sub-catalog (color, restyle, repair),
  // they must also pick a tier (or explicitly "Not sure") before the
  // flow advances. Stops "wash + color" bookings landing on Rivky's
  // calendar with no idea which color tier the customer wanted.
  const SUB_TIER_REQUIRED_SERVICES = new Set(["color", "restyle", "repair"]);
  const subTierComplete = (w: WigLine): boolean => {
    for (const svcId of w.selectedServices) {
      if (!SUB_TIER_REQUIRED_SERVICES.has(svcId)) continue;
      const pick = w.subTierByService?.[svcId];
      if (!pick) return false;
    }
    return true;
  };

  const canGoNext = (): boolean => {
    switch (currentStep) {
      case "service":
        // At least one of: new-wig consult, OR any wig has services + location.
        if (wantsNewWigConsult && wigs.every((w) => w.selectedServices.length === 0)) return true;
        return wigs.some(
          (w) => w.selectedServices.length > 0 && w.location !== null && subTierComplete(w)
        ) && wigs.every((w) =>
          // Each wig that has services must have its tiers picked.
          w.selectedServices.length === 0 || subTierComplete(w)
        );
      case "schedule":
        return !!date && !!time;
      case "dropoff":
        // A drop-off date is required ONLY when the customer is
        // bringing it in themselves. When Uber picks it up, the salon
        // coordinates timing, so the date is optional and we don't
        // gate on it (Rivky 2026-06-28).
        if (!uberDropoff && !dropDate) return false;
        // Scheduled Uber needs both a date and a time picked.
        if (uberDropoff && uberSchedule && (!uberScheduleDate || !uberScheduleTime)) return false;
        // Courier path requires a complete pickup address — without
        // it /api/service-pickup returns 400. Block the Continue
        // button instead of letting the customer hit submit and fail.
        if (wantsCourier) {
          return (
            !!pickupStreet.trim() &&
            !!pickupCity.trim() &&
            !!pickupState.trim() &&
            !!pickupZip.trim()
          );
        }
        return true;
      case "details":
        return !!name.trim() && !!email.trim();
      case "confirm":
        return true;
    }
  };

  // Why the Continue button is blocked right now, in words the
  // customer can act on. A live customer (2026-07-05) sat on a
  // silently-disabled Continue for two minutes — she'd picked a
  // service + uploaded a photo but missed the location/tier pick —
  // then gave up and told the chat "the site didn't let me book."
  const blockReason = (): string | null => {
    if (canGoNext()) return null;
    switch (currentStep) {
      case "service": {
        const withServices = wigs.filter((w) => w.selectedServices.length > 0);
        if (withServices.length === 0) return t("ub_blockPickService");
        if (withServices.some((w) => !subTierComplete(w))) return t("ub_blockPickSubTier");
        if (withServices.some((w) => w.location === null)) return t("ub_blockPickLocation");
        return t("ub_blockPickService");
      }
      case "schedule":
        return t("ub_blockPickDateTime");
      case "dropoff":
        if (!uberDropoff && !dropDate) return t("ub_blockPickDropoffDate");
        if (uberDropoff && uberSchedule && (!uberScheduleDate || !uberScheduleTime))
          return t("ub_blockUberSchedule");
        return t("ub_blockPickupAddress");
      case "details":
        return t("ub_blockNameEmail");
      default:
        return t("ub_validationFillEverything");
    }
  };

  // ─── Step navigation ───────────────────────────────────────────────
  const goNext = () => {
    const idx = stepsList.indexOf(currentStep);
    if (idx < stepsList.length - 1) {
      setCurrentStep(stepsList[idx + 1]);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };
  const goBack = () => {
    const idx = stepsList.indexOf(currentStep);
    if (idx > 0) {
      setCurrentStep(stepsList[idx - 1]);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  // ─── Submit ────────────────────────────────────────────────────────
  const handleSubmit = async (
    sourceId?: string,
    serviceFee?: { sourceId: string; amountDollars: number }
  ) => {
    const badStep = firstInvalidStep();
    if (badStep !== null) {
      toast(t("ub_validationFillEverything"), "error");
      // Jump the customer back to the step with the missing field
      // instead of leaving them stranded on the Confirm screen.
      if (badStep !== currentStep) {
        setCurrentStep(badStep);
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
      return;
    }
    setSubmitting(true);

    // Hoisted so the catch below can report res.status when the response
    // arrived but wasn't JSON (gateway error page) vs. a network reject.
    let res: Response | undefined;
    try {
      // Per-wig photos flow to the appropriate side of the booking:
      //   • drop-off wigs → serviceItems[i].photos on the
      //     ServicePickup row. Server aggregates them into
      //     pickup.photos.intake so existing admin/customer views
      //     keep working.
      //   • appointment-mode wigs → flatten their photos into
      //     appointment.attachments (string[]), saved on the
      //     Appointment row. Customer sees them on /account/
      //     appointments; admin sees them on /admin/appointments.
      const dropoffWigs = wigs.filter(
        (w) => w.location === "dropoff" && w.selectedServices.length > 0
      );
      const appointmentWigs = wigs.filter(
        (w) => w.location === "appointment" && w.selectedServices.length > 0
      );
      const appointmentAttachments = appointmentWigs.flatMap((w) => w.photos);

      // Append picked sub-tier names per wig to the customer notes so
      // admin sees the specific tier in the appointment / pickup
      // detail without needing a new schema field. Format:
      //   "Wig 1 — Color: Highlights · Cut: Layered"
      const formatTierNotes = (group: typeof dropoffWigs): string => {
        const lines: string[] = [];
        group.forEach((w, i) => {
          const tiers = w.subTierByService || {};
          const picks = Object.entries(tiers)
            .filter(([, v]) => v)
            .map(([svcId, v]) => {
              const def = SERVICES.find((s) => s.id === svcId);
              const display = v === "__not_sure" ? "Not sure (recommend on-site)" : v;
              return `${def?.label || svcId}: ${display}`;
            });
          if (picks.length > 0) {
            const label = w.wigName || `Wig ${i + 1}`;
            lines.push(`${label} — ${picks.join(" · ")}`);
          }
        });
        return lines.join("\n");
      };
      const dropoffTierNotes = formatTierNotes(dropoffWigs);
      const appointmentTierNotes = formatTierNotes(appointmentWigs);
      // When the customer wants a styling appointment once the wash &
      // set is done, we can't book a hard slot now (timing depends on
      // when the work finishes). Flag it in the ticket notes so the
      // salon reaches out to schedule styling when it's ready.
      const stylingNote = wantsStyling
        ? "Customer would like a styling appointment once the wash & set is done — please reach out to schedule when it's ready."
        : "";
      const dropoffCombinedNotes = [notes, dropoffTierNotes, stylingNote]
        .filter(Boolean)
        .join("\n\n");
      const appointmentCombinedNotes = [notes, appointmentTierNotes]
        .filter(Boolean)
        .join("\n\n");

      const servicePickup = needsPickup
        ? {
            serviceItems: dropoffWigs.map((w) => ({
              wigName: w.wigName || undefined,
              services: w.selectedServices,
              photos: w.photos.length > 0 ? w.photos : undefined,
              subTiers: w.subTierByService,
            })),
            // Date is optional when Uber handles pickup — default to
            // today so the API (which requires a pickupDate) accepts it;
            // the salon coordinates the actual Uber window. UTC-today is
            // never before studio-today (NY is behind UTC), so the
            // server's future-date check passes.
            pickupDate: dropDate || new Date().toISOString().slice(0, 10),
            pickupPhone: phone,
            wantsCourierDropoff: uberDropoff,
            wantsCourierReturn: uberReturn,
            // Deferred Uber dispatch — only sent when the customer
            // explicitly scheduled; absent = dispatch immediately.
            ...(uberDropoff && uberSchedule && uberScheduleDate && uberScheduleTime
              ? {
                  courierScheduledDate: uberScheduleDate,
                  courierScheduledTime: uberScheduleTime,
                }
              : {}),
            // Forward the address fields when EITHER courier leg is
            // selected. /api/service-pickup requires them in that path;
            // for walk-in drop-off they stay blank (admin can add later).
            ...(wantsCourier
              ? {
                  pickupAddress: pickupStreet,
                  pickupCity,
                  pickupState,
                  pickupZip,
                }
              : {}),
            notes: dropoffCombinedNotes || undefined,
          }
        : undefined;

      const appointment = needsAppointment && appointmentType
        ? {
            type: appointmentType,
            additionalTypes: additionalTypes.length > 0 ? additionalTypes : undefined,
            date,
            startTime: time,
            notes: appointmentCombinedNotes || undefined,
            attachments: appointmentAttachments.length > 0 ? appointmentAttachments : undefined,
            ...(slotReservation.reservation
              ? { reservationId: slotReservation.reservation.id }
              : {}),
          }
        : undefined;

      const deposit =
        sourceId && depositAmount && depositAmount > 0
          ? { sourceId, amountDollars: depositAmount }
          : undefined;

      res = await fetch("/api/book", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name,
          email,
          phone,
          smsOptIn,
          appointment,
          servicePickup,
          deposit,
          // Manny #148 — separate from `deposit` so the new-wig
          // deposit logic stays untouched. /api/book charges this
          // amount via Square and stamps Appointment.serviceFeeStatus
          // = "paid". When omitted, status defaults to null (skip
          // path) or "pending_at_appointment" (require path failure).
          serviceFee,
        }),
      });
      const data = await res.json();

      if (!res.ok && res.status !== 207) {
        if (data.appointmentError === "reservation_expired") {
          slotReservation.markExpired();
          setSubmitting(false);
          return;
        }
        // Time was taken between picking it and confirming. Send the
        // customer back to the calendar with a clear inline message
        // instead of a toast reading "slot_full".
        if (data.appointmentError === "slot_full") {
          void slotReservation.release();
          setTime("");
          setSlotTakenNotice(t("ub_slotTakenNotice"));
          setCurrentStep("schedule");
          window.scrollTo({ top: 0, behavior: "smooth" });
          setSubmitting(false);
          return;
        }
        // pickupError included — /api/book returns 400 with ONLY
        // pickupError set when the drop-off half is rejected; omitting
        // it here masked the real reason behind a generic "Booking
        // failed" (Ariel, 2026-07-19).
        toast(data.error || data.appointmentError || data.pickupError || t("ub_toastBookingFailed"), "error");
        setSubmitting(false);
        return;
      }

      if (res.status === 207) {
        const partialMsg = data.pickupError
          ? t("ub_toastDropoffFailed", { error: data.pickupError })
          : t("ub_toastAppointmentFailed", {
              error:
                data.appointmentError === "slot_full"
                  ? t("ub_slotTakenNotice")
                  : data.appointmentError,
            });
        toast(partialMsg, "info");
      } else {
        const apptStatus = data.appointment?.status;
        if (apptStatus === "pending") {
          toast(t("ub_toastRequestReceived"), "success");
        } else {
          toast(t("ub_toastBookingConfirmed"), "success");
        }
      }

      // Persist pickup address as the customer's saved default when
      // they ticked the toggle (default ON for first-time customers,
      // OFF for returning ones). Best-effort; submit success doesn't
      // hinge on this.
      if (
        wantsCourier &&
        savePickupAsDefault &&
        pickupStreet.trim() &&
        pickupCity.trim() &&
        pickupState.trim() &&
        pickupZip.trim() &&
        session?.user?.id
      ) {
        try {
          await fetch("/api/account/addresses", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              id: savedPickupAddressId || undefined,
              line1: pickupStreet.trim(),
              city: pickupCity.trim(),
              state: pickupState.trim(),
              zip: pickupZip.trim(),
              isDefault: true,
            }),
          });
        } catch {
          /* silent — booking already succeeded */
        }
      }

      // Show inline success state. /account/* are re-entrant booking
      // forms — landing on them post-submit looked like "the next
      // booking page" to early testers. The success card has links to
      // the management views for customers who do want to go there.
      setBooked({
        appointmentNumber: data.appointment?.appointmentNumber ?? null,
        pickupNumber: data.servicePickup?.pickupNumber ?? null,
        appointmentStatus: data.appointment?.status ?? null,
        pickupId: data.servicePickup?.id ?? null,
        // The send-in leg auto-dispatches a real Uber immediately —
        // UNLESS the customer scheduled it for later (cron dispatches).
        courierDispatched: uberDropoff && !uberSchedule && !!data.servicePickup?.id,
        courierTrackingUrl: data.servicePickup?.trackingUrl ?? null,
        courierScheduledLabel:
          uberDropoff && uberSchedule && uberScheduleDate && uberScheduleTime && data.servicePickup?.id
            ? `${new Date(uberScheduleDate + "T00:00:00").toLocaleDateString("en-US", {
                weekday: "short",
                month: "short",
                day: "numeric",
              })} at ${(() => {
                const [h, m] = uberScheduleTime.split(":").map(Number);
                const h12 = h % 12 || 12;
                return `${h12}:${String(m).padStart(2, "0")} ${h < 12 ? "AM" : "PM"}`;
              })()}`
            : null,
      });
      setCourierCancelState("idle");
      setSubmitting(false);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      // /api/book always returns JSON (even on 500), so landing here means
      // the response body wasn't JSON (gateway 502/504 error page) or the
      // network dropped. These failures previously left zero server-side
      // trace — report them so /admin/errors sees the next one.
      fetch("/api/errors", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          message: `Booking submit failed on client: ${err instanceof Error ? err.message : String(err)}`,
          stack: err instanceof Error ? err.stack : undefined,
          url: window.location.href,
          userAgent: navigator.userAgent,
          context: {
            kind: "booking-submit",
            // status present = response arrived but body wasn't JSON;
            // absent = the fetch itself rejected (network drop).
            status: res?.status,
            needsPickup,
            needsAppointment,
            hadServiceFee: Boolean(serviceFee),
            hadDeposit: Boolean(sourceId),
          },
        }),
        keepalive: true,
      }).catch(() => {});
      toast(t("ub_toastBookingFailedRetry"), "error");
      setSubmitting(false);
    }
  };

  const canSubmit = (): boolean => firstInvalidStep() === null;

  // Returns the earliest step in the flow that has a validation
  // problem, so handleSubmit can JUMP the customer back to it
  // instead of just showing an opaque toast (#159 family).
  const firstInvalidStep = (): Step | null => {
    // service: at least one wig with services AND every wig that
    // picked services chose a location (drop-off vs appointment).
    if (!needsAppointment && !needsPickup) return "service";
    for (const w of wigs) {
      if (w.selectedServices.length === 0) continue;
      if (!w.location) return "service";
    }
    // schedule: appointment date+time
    if (needsAppointment && (!date || !time)) return "schedule";
    // dropoff: self drop-off needs a date; scheduled Uber needs date + time
    if (needsPickup && !uberDropoff && !dropDate) return "dropoff";
    if (
      needsPickup &&
      uberDropoff &&
      uberSchedule &&
      (!uberScheduleDate || !uberScheduleTime)
    )
      return "dropoff";
    // details: name + email
    if (!name.trim() || !email.trim()) return "details";
    return null;
  };

  // ─── Render ────────────────────────────────────────────────────────

  const stepLabel: Record<Step, string> = {
    service: t("ub_stepService"),
    schedule: t("ub_stepSchedule"),
    dropoff: t("ub_stepDropoff"),
    details: t("ub_stepDetails"),
    confirm: t("ub_stepConfirm"),
  };

  const currentIdx = stepsList.indexOf(currentStep);

  // ─── Success state ─────────────────────────────────────────────────
  // Renders inline once a successful submit returns. Replaces the
  // earlier redirect to /account/* (those routes are re-entrant
  // booking forms; landing on them looked like "the next booking page"
  // to testers).
  if (booked) {
    const apptPending = booked.appointmentStatus === "pending";
    return (
      <>
        <Header />
        <main>
      <div className="mx-auto max-w-2xl px-4 py-16 text-center">
        <div className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-emerald-100 mb-4">
          <CheckCircle2 className="h-7 w-7 text-emerald-600" />
        </div>
        <h1 className="font-display text-3xl tracking-tight text-brand-dark mb-3">
          {apptPending ? t("ub_successRequestReceivedTitle") : t("ub_successBookedTitle")}
        </h1>
        <p className="text-sm text-brand-muted leading-relaxed mb-6">
          {apptPending
            ? t("ub_successPendingMessage")
            : t("ub_successConfirmedMessage")}
        </p>
        <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm text-left mb-6 space-y-2">
          {booked.appointmentNumber && (
            <div className="flex items-center justify-between gap-3 text-sm">
              <span className="text-brand-muted">{t("ub_successAppointmentLabel")}</span>
              <span className="font-medium text-brand-dark">
                {booked.appointmentNumber}
              </span>
            </div>
          )}
          {booked.pickupNumber && (
            <div className="flex items-center justify-between gap-3 text-sm">
              <span className="text-brand-muted">{t("ub_successDropoffLabel")}</span>
              <span className="font-medium text-brand-dark">
                {booked.pickupNumber}
              </span>
            </div>
          )}
        </div>
        {/* Scheduled Uber — no courier yet; cron dispatches at the
            chosen time. Point the customer at the manage page. */}
        {booked.courierScheduledLabel && (
          <div className="mb-6 rounded-lg border border-brand-gold/40 bg-brand-cream/50 px-4 py-3 text-left text-sm text-brand-dark">
            <p className="font-medium mb-1">
              {t("ub_successUberScheduled", { when: booked.courierScheduledLabel })}
            </p>
            <p className="text-xs text-brand-muted">
              {t("ub_successUberScheduledHint")}{" "}
              <Link href="/account/service-pickup" className="text-brand-gold underline">
                {t("ub_successUberScheduledManage")}
              </Link>
            </p>
          </div>
        )}
        {/* Live courier tracking — in-site map of the Uber en route. */}
        {booked.courierDispatched && booked.pickupId && courierCancelState !== "canceled" && (
          <div className="mb-4 text-left">
            <CourierTrackMap
              pickupId={booked.pickupId}
              trackingUrl={booked.courierTrackingUrl}
            />
          </div>
        )}

        {/* Immediate courier cancel — only when the send-in Uber was
            auto-dispatched. Lets a customer who mis-booked stop the
            driver right away (a fee may apply if it's already moving). */}
        {booked.courierDispatched && booked.pickupId && (
          <div className="mb-6">
            {courierCancelState === "canceled" ? (
              <p className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-2.5 text-sm text-emerald-800">
                Uber pickup cancelled. If the driver had already started, a small Uber fee may still apply.
              </p>
            ) : (
              <>
                <button
                  onClick={async () => {
                    if (!booked.pickupId) return;
                    setCourierCancelState("canceling");
                    try {
                      const res = await fetch(
                        `/api/service-pickup/${booked.pickupId}/cancel-courier`,
                        { method: "POST" }
                      );
                      const d = await res.json().catch(() => ({}));
                      if (res.ok) {
                        setCourierCancelState("canceled");
                      } else {
                        setCourierCancelState("error");
                        toast(d.error || "Couldn't cancel the Uber — please contact us.", "error");
                      }
                    } catch {
                      setCourierCancelState("error");
                      toast("Couldn't cancel the Uber — please contact us.", "error");
                    }
                  }}
                  disabled={courierCancelState === "canceling"}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-red-300 px-4 py-2.5 text-sm font-medium text-red-600 hover:bg-red-50 transition-colors disabled:opacity-50"
                >
                  {courierCancelState === "canceling" && (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  )}
                  Cancel Uber pickup
                </button>
                <p className="mt-1.5 text-[11px] text-brand-muted">
                  Changed your mind? Cancel now. A small Uber fee may apply if the driver is already on the way.
                </p>
              </>
            )}
          </div>
        )}
        <div className="flex flex-col sm:flex-row justify-center gap-3 text-sm">
          {booked.appointmentNumber && (
            <Link
              href="/account/appointments"
              className="rounded-lg border border-gray-200 px-4 py-2.5 text-brand-muted hover:bg-gray-50 transition-colors"
            >
              {t("ub_successViewAppointments")}
            </Link>
          )}
          {booked.pickupNumber && !booked.appointmentNumber && (
            <Link
              href="/account/service-pickup?tab=history"
              className="rounded-lg border border-gray-200 px-4 py-2.5 text-brand-muted hover:bg-gray-50 transition-colors"
            >
              {t("ub_successViewDropoffs")}
            </Link>
          )}
          <Link
            href="/"
            className="rounded-lg bg-brand-dark px-4 py-2.5 text-white hover:bg-brand-gold transition-colors"
          >
            {t("ub_successBackToHome")}
          </Link>
        </div>
      </div>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Header />
      <main>
    <div className="mx-auto max-w-5xl px-4 py-12 pb-32">
      {slotReservation.expired && (
        <SlotReservationExpiredModal
          onRetry={slotReservation.retry}
          onPickNew={() => {
            slotReservation.clearExpired();
            setTime("");
            toast(t("ub_toastPickNewTime"), "info");
          }}
        />
      )}

      <div className="relative mb-8 overflow-hidden rounded-2xl">
        {/* Two-image consultation banner behind the header */}
        <div className="grid h-44 grid-cols-2 sm:h-56">
          <div className="relative">
            <Image
              src="/images/pages/book-1.jpg"
              alt=""
              fill
              priority
              sizes="(max-width: 768px) 50vw, 320px"
              className="object-cover object-center"
            />
          </div>
          <div className="relative">
            <Image
              src="/images/pages/book-2.jpg"
              alt=""
              fill
              priority
              sizes="(max-width: 768px) 50vw, 320px"
              className="object-cover object-center"
            />
          </div>
        </div>
        <div className="absolute inset-0 bg-brand-dark/55" />
        <div className="absolute inset-0 flex flex-col items-center justify-center px-4 text-center">
          <p className="mb-2 text-[11px] font-medium uppercase tracking-[0.2em] text-brand-gold">
            {t("ub_pageEyebrow")}
          </p>
          <h1 className="font-display text-3xl tracking-tight text-white sm:text-4xl">
            {stepLabel[currentStep]}
          </h1>
          {/* Step indicator */}
          <div className="mt-4 flex justify-center gap-1">
            {stepsList.map((s, i) => (
              <span
                key={s}
                className={`h-1 w-8 rounded-full transition-colors ${
                  i <= currentIdx ? "bg-brand-gold" : "bg-white/30"
                }`}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Two-column layout: step content + sticky right summary on
          desktop. Mobile collapses the summary to a top card via
          BookingSummary's internal responsive logic. */}
      <div className="grid gap-8 md:grid-cols-[1fr_280px]">
        <div>

      {/* ─── Step 1: Service selection ─── */}
      {currentStep === "service" && (
        <div className="animate-fade-in space-y-6">
          {/* Intent-aware layout (Rivky #151):
                - service intent (default) → service picker prominent,
                  consultation hidden behind a "+ Also need new wig
                  consultation?" link at the bottom.
                - consultation intent → consultation card prominent,
                  service list hidden behind a "+ Also need servicing
                  on a current wig?" link.
              Same components in both modes; only the visibility-by-
              default + order differs. The customer can flip both on
              for a combined visit. */}
          {(isConsultationIntent || showConsultAddon) && (
          <section ref={consultSectionRef}>
            <button
              type="button"
              onClick={() => setWantsNewWigConsult(!wantsNewWigConsult)}
              className={`w-full text-left rounded-xl border p-4 transition-all ${
                wantsNewWigConsult
                  ? "border-brand-gold bg-brand-gold/5 shadow-sm"
                  : "border-gray-200 bg-white hover:border-brand-gold/40 hover:bg-brand-cream/30"
              }`}
              aria-pressed={wantsNewWigConsult}
            >
              <div className="flex items-start gap-3">
                <div
                  className={`flex-shrink-0 mt-0.5 w-10 h-10 rounded-full flex items-center justify-center transition-colors ${
                    wantsNewWigConsult
                      ? "bg-brand-gold text-white"
                      : "bg-brand-cream text-brand-gold"
                  }`}
                >
                  <Crown className="h-5 w-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline justify-between gap-2 flex-wrap">
                    <span className="font-medium text-brand-dark text-sm">
                      {t("ub_consultTitle")}
                    </span>
                    <span
                      className={`text-xs font-medium ${
                        wantsNewWigConsult ? "text-brand-gold-dark" : "text-brand-muted"
                      }`}
                    >
                      {t("ub_consultDeposit")}
                    </span>
                  </div>
                  <p className="mt-1 text-[11px] text-brand-muted leading-relaxed">
                    {t("ub_consultDescription")}
                  </p>
                  {wantsNewWigConsult && (
                    <div className="mt-2 flex items-center gap-3 text-[10px] text-brand-muted/90">
                      <span className="inline-flex items-center gap-1">
                        <Sparkles className="h-3 w-3" />
                        {t("ub_consultBadgeCustom")}
                      </span>
                      <span>{t("ub_consultBadgeDuration")}</span>
                    </div>
                  )}
                </div>
                <div
                  className={`flex-shrink-0 mt-1 w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all ${
                    wantsNewWigConsult ? "border-brand-gold bg-brand-gold" : "border-gray-300"
                  }`}
                  aria-hidden="true"
                >
                  {wantsNewWigConsult && <Check className="h-3 w-3 text-white" />}
                </div>
              </div>
            </button>
          </section>
          )}

          {/* Multi-wig service picker — gated by intent */}
          {(!isConsultationIntent || showServiceAddon) && (
            <div ref={wigListSectionRef}>
              <WigList value={wigs} setValue={setWigs} />
            </div>
          )}

          {/* Cross-link: surface the OTHER mode as a small expandable
              link if it isn't already showing. Auto-selects + scrolls
              into view (#159 — was just toggling visibility 700px up
              the page where the customer couldn't see it). */}
          {!isConsultationIntent && !showConsultAddon && (
            <div className="text-center">
              <button
                type="button"
                onClick={() => {
                  setShowConsultAddon(true);
                  setWantsNewWigConsult(true);
                  // Wait one paint so the section mounts before we
                  // measure / scroll. requestAnimationFrame is more
                  // reliable than setTimeout(0) in React.
                  requestAnimationFrame(() => {
                    requestAnimationFrame(() => {
                      consultSectionRef.current?.scrollIntoView({
                        behavior: "smooth",
                        block: "center",
                      });
                    });
                  });
                }}
                className="inline-flex items-center gap-1 text-xs text-brand-muted hover:text-brand-gold-dark transition-colors"
              >
                <Plus className="h-3 w-3" />
                {t("ub_addConsultLink")}
              </button>
            </div>
          )}
          {isConsultationIntent && !showServiceAddon && (
            <div className="text-center">
              <button
                type="button"
                onClick={() => {
                  setShowServiceAddon(true);
                  requestAnimationFrame(() => {
                    requestAnimationFrame(() => {
                      wigListSectionRef.current?.scrollIntoView({
                        behavior: "smooth",
                        block: "start",
                      });
                    });
                  });
                }}
                className="inline-flex items-center gap-1 text-xs text-brand-muted hover:text-brand-gold-dark transition-colors"
              >
                <Plus className="h-3 w-3" />
                {t("ub_addServiceLink")}
              </button>
            </div>
          )}
        </div>
      )}

      {/* ─── Step 2: Schedule (if needed) ─── */}
      {currentStep === "schedule" && (
        <DatetimeStep
          appointmentType={appointmentType}
          additionalTypes={additionalTypes}
          notice={slotTakenNotice}
          date={date}
          setDate={setDate}
          time={time}
          setTime={setTime}
          slotReservation={slotReservation}
          bookingWindowDays={bookingWindowDays}
          holidayExtensionDates={holidayExtensionDates}
          closedDays={closedDays}
          allowedTypesByDay={allowedTypesByDay}
          primaryLabel={selectedServiceData?.label || t("ub_appointmentFallbackLabel")}
          secondaryLabel={
            additionalTypes.length > 0
              ? additionalTypes
                  .map((id) => SERVICES.find((s) => s.id === id)?.label)
                  .filter(Boolean)
                  .join(" + ")
              : undefined
          }
          name={name}
          email={email}
          phone={phone}
          dropoffDays={dropoffDaysByType[appointmentType] ?? 0}
          trackDateEventName="booking_date_selected"
          trackTimeEventName="booking_time_selected"
        />
      )}

      {/* ─── Step 3: Drop-off date (if needed) ─── */}
      {currentStep === "dropoff" && (
        <>
          <DropoffSection
            dropDate={dropDate}
            setDropDate={setDropDate}
            uberDropoff={uberDropoff}
            setUberDropoff={setUberDropoff}
            uberReturn={uberReturn}
            setUberReturn={setUberReturn}
            wantsStyling={wantsStyling}
            setWantsStyling={setWantsStyling}
            dropoffWigCount={dropoffWigCount}
            dropoffDaysByType={dropoffDaysByType}
            uberSchedule={uberSchedule}
            setUberSchedule={setUberSchedule}
            uberScheduleDate={uberScheduleDate}
            setUberScheduleDate={setUberScheduleDate}
            uberScheduleTime={uberScheduleTime}
            setUberScheduleTime={setUberScheduleTime}
            selectedDropoffServiceIds={Array.from(
              new Set(
                wigs
                  .filter((w) => w.location === "dropoff")
                  .flatMap((w) => w.selectedServices)
              )
            )}
          />

          {/* Pickup address — only shown when customer wants courier
              dispatch. Without this, /api/service-pickup rejects the
              booking with "Pickup address and phone are required for
              courier service". Auto-fills from the customer's saved
              default Address; PlaceAutocomplete drives the input. */}
          {wantsCourier && (
            <section className="mt-4 rounded-xl border border-gray-200 bg-white p-5 shadow-sm space-y-3">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h3 className="text-sm font-semibold text-brand-dark">
                    Your address
                  </h3>
                  <p className="text-xs text-brand-muted mt-0.5">
                    {uberDropoff && uberReturn
                      ? "Where the courier picks up and returns your wig."
                      : uberDropoff
                        ? "Where the courier will collect your wig."
                        : "Where the courier will return your wig when it's ready."}
                  </p>
                </div>
                {pickupAddressAutoFilled && (
                  <span className="text-[10px] uppercase tracking-wider font-medium text-emerald-700">
                    ✓ From your saved address
                  </span>
                )}
              </div>
              <PlaceAutocomplete
                required
                initialDisplay={pickupStreet}
                value={{
                  street1: pickupStreet,
                  city: pickupCity,
                  state: pickupState,
                  zip: pickupZip,
                }}
                onChange={(next) => {
                  setPickupStreet(next.street1);
                  setPickupCity(next.city);
                  setPickupState(next.state);
                  setPickupZip(next.zip);
                  // User edited after auto-fill — clear the banner so
                  // the helper text below changes.
                  if (pickupAddressAutoFilled) setPickupAddressAutoFilled(false);
                }}
              />
              {/* Live Uber quote — real fee before they finalize. */}
              {quoteLoading && (
                <p className="text-xs text-brand-muted">Getting live Uber quote…</p>
              )}
              {!quoteLoading && uberQuote && (
                <div className="rounded-lg border border-brand-gold/40 bg-brand-cream/50 px-3 py-2 text-xs text-brand-dark">
                  <span className="font-semibold">
                    Live Uber quote: {formatPrice(uberQuote.fee)} each way
                  </span>
                  {uberDropoff && uberReturn && (
                    <span>
                      {" "}· {formatPrice(uberQuote.fee * 2)} total for pickup + return
                    </span>
                  )}
                  <span className="block mt-0.5 text-[11px] text-brand-muted">
                    Final fee is what Uber charges at dispatch; charged at finalization.
                  </span>
                </div>
              )}
              {session?.user?.id && (
                <label className="flex items-start gap-2 cursor-pointer text-xs text-brand-muted">
                  <input
                    type="checkbox"
                    checked={savePickupAsDefault}
                    onChange={(e) => setSavePickupAsDefault(e.target.checked)}
                    className="mt-0.5 rounded border-gray-300 text-brand-gold focus:ring-brand-gold"
                  />
                  <span>
                    Save this address as my default — next booking will auto-fill it.
                  </span>
                </label>
              )}
            </section>
          )}
        </>
      )}

      {/* ─── Step 4: Details (contact + photos + notes) ─── */}
      {currentStep === "details" && (
        <div className="animate-fade-in space-y-6">
          <section className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm space-y-6">
            <ContactStep
              name={name}
              setName={setName}
              email={email}
              setEmail={setEmail}
              phone={phone}
              setPhone={setPhone}
              smsOptIn={smsOptIn}
              setSmsOptIn={setSmsOptIn}
            />
            <div className="w-full">
              <label className="mb-1.5 block text-xs font-medium uppercase tracking-widest text-brand-muted">
                {t("ub_notesLabel")}
              </label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder={t("ub_notesPlaceholder")}
                rows={4}
                className="w-full border-0 border-b border-brand-dark/20 bg-transparent px-0 py-2.5 text-sm text-brand-dark placeholder:text-brand-muted/50 focus:border-brand-gold focus:outline-none focus:ring-0 resize-none"
              />
            </div>
            {/* Per-wig reference photos live INSIDE each WigBlock —
                attached to the right wig at booking time so admin sees
                them next to the wig name on the detail panel. Removed
                the global UploadSection here so customers in mixed
                bookings don't have to guess which wig the photos
                belong to. */}
          </section>
        </div>
      )}

      {/* ─── Step 5: Confirm ─── */}
      {currentStep === "confirm" && (
        <div className="animate-fade-in space-y-6">
          <section className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
            <h2 className="text-base font-semibold text-brand-dark mb-3">
              {t("ub_reviewTitle")}
            </h2>
            <div className="space-y-3 text-sm">
              {wantsNewWigConsult && (
                <div>
                  <span className="text-brand-muted">{t("ub_reviewServiceLabel")}</span>{" "}
                  <span className="text-brand-dark">
                    {t("ub_reviewServiceConsult")}
                  </span>
                </div>
              )}
              {hasInPersonWig && (
                <div>
                  <span className="text-brand-muted">{t("ub_reviewInSalonLabel")}</span>{" "}
                  <span className="text-brand-dark">
                    {wigs
                      .filter(
                        (w) =>
                          w.location === "appointment" &&
                          w.selectedServices.length > 0
                      )
                      .map((w, i) =>
                        t("ub_reviewWigServices", {
                          wigLabel: w.wigName || t("ub_wigDefaultLabel", { index: i + 1 }),
                          services: w.selectedServices
                            .map(
                              (id) =>
                                SERVICES.find((s) => s.id === id)?.label || id
                            )
                            .join(", "),
                        })
                      )
                      .join(" · ")}
                  </span>
                </div>
              )}
              {needsAppointment && date && time && (
                <div>
                  <span className="text-brand-muted">{t("ub_reviewWhenLabel")}</span>{" "}
                  <span className="text-brand-dark">
                    {t("ub_reviewWhenAt", {
                      date: new Date(date + "T12:00:00").toLocaleDateString("en-US", {
                        weekday: "short",
                        month: "short",
                        day: "numeric",
                      }),
                      time,
                    })}
                  </span>
                </div>
              )}
              {needsPickup && (
                <div>
                  <span className="text-brand-muted">{t("ub_reviewDropoffLabel")}</span>{" "}
                  <span className="text-brand-dark">
                    {wigs
                      .filter(
                        (w) =>
                          w.location === "dropoff" &&
                          w.selectedServices.length > 0
                      )
                      .map((w, i) =>
                        t("ub_reviewWigServices", {
                          wigLabel: w.wigName || t("ub_wigDefaultLabel", { index: i + 1 }),
                          services: w.selectedServices
                            .map(
                              (id) =>
                                SERVICES.find((s) => s.id === id)?.label || id
                            )
                            .join(", "),
                        })
                      )
                      .join(" · ")}
                  </span>
                  {(dropDate || wantsCourier) && (
                    <span className="text-brand-muted">
                      {dropDate &&
                        t("ub_reviewDropoffOn", {
                          date: new Date(dropDate + "T12:00:00").toLocaleDateString(
                            "en-US",
                            { weekday: "short", month: "short", day: "numeric" }
                          ),
                        })}
                      {uberDropoff
                        ? t("ub_reviewMethodUberIn")
                        : dropDate
                          ? t("ub_reviewMethodInPerson")
                          : ""}
                      {uberReturn && t("ub_reviewMethodUberReturn")}
                    </span>
                  )}
                  {wantsStyling && (
                    <span className="block text-brand-muted">
                      {t("ub_reviewStyling")}
                    </span>
                  )}
                </div>
              )}
              <div>
                <span className="text-brand-muted">{t("ub_reviewForLabel")}</span>{" "}
                <span className="text-brand-dark">
                  {t("ub_reviewForValue", { name, email })}
                </span>
              </div>
            </div>
          </section>

          <DepositCard
            amount={depositAmount}
            submitting={submitting}
            slotReservation={slotReservation}
            onPay={handleSubmit}
            onPaymentError={(msg) => toast(msg, "error")}
            payingDeposit={payingDeposit}
            setPayingDeposit={setPayingDeposit}
          />

          <ServiceFeeCard
            services={serviceFeeServices}
            submitting={submitting}
            paying={payingServiceFee}
            setPaying={setPayingServiceFee}
            onPay={(srcId, total) =>
              handleSubmit(undefined, { sourceId: srcId, amountDollars: total })
            }
            onSkip={() => handleSubmit()}
            onPaymentError={(msg) => toast(msg, "error")}
          />
        </div>
      )}

      {/* ─── Navigation ─── */}
      <div className="mt-10 pt-6 border-t border-gray-100">
        <div className="flex items-center justify-between">
          {currentIdx > 0 ? (
            <button
              onClick={goBack}
              className="flex items-center gap-2 text-sm text-brand-muted hover:text-brand-dark transition-colors font-medium"
            >
              <ChevronLeft className="h-4 w-4" />
              {t("ub_navBack")}
            </button>
          ) : (
            <Link
              href="/account"
              className="text-sm text-brand-muted hover:text-brand-dark transition-colors font-medium"
            >
              {t("ub_navCancel")}
            </Link>
          )}

          {currentStep === "confirm" ? (
            // When DepositCard or ServiceFeeCard is active, that card
            // owns the submit control — suppress the plain button so
            // we don't double-render two ways to confirm.
            (depositAmount && depositAmount > 0) || serviceFeeServices.length > 0 ? (
              <div />
            ) : (
              <div className="flex items-center gap-3">
                {slotReservation.reservation && (
                  <div className="max-w-xs">
                    <SlotReservationBanner
                      expiresAt={slotReservation.reservation.expiresAt}
                      onExpired={slotReservation.markExpired}
                    />
                  </div>
                )}
                <button
                  onClick={() => handleSubmit()}
                  disabled={submitting || !canSubmit()}
                  className="inline-flex items-center gap-1.5 rounded-lg bg-brand-dark px-4 py-2 text-sm font-medium text-white hover:bg-brand-gold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
                  {t("ub_navConfirmBooking")}
                  <ArrowRight className="h-4 w-4" />
                </button>
              </div>
            )
          ) : (
            <button
              // Stays clickable when incomplete so we can TELL the
              // customer what's missing — a disabled button swallows
              // the tap and reads as "the site is broken."
              onClick={() => {
                const reason = blockReason();
                if (reason) {
                  toast(reason, "error");
                  return;
                }
                goNext();
              }}
              className={`inline-flex items-center gap-1.5 rounded-lg bg-brand-dark px-4 py-2 text-sm font-medium text-white hover:bg-brand-gold transition-colors ${
                canGoNext() ? "" : "opacity-50"
              }`}
            >
              {t("ub_navContinue")}
              <ArrowRight className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>
        </div>

        {/* Sticky running-summary sidebar (desktop) /
            collapsible "Your booking" card (mobile). */}
        <BookingSummary
          wigs={wigs}
          wantsNewWigConsult={wantsNewWigConsult}
          date={date}
          time={time}
          dropDate={dropDate}
          wantsCourier={wantsCourier}
          depositAmount={depositAmount}
        />
      </div>
    </div>
      </main>
      <Footer />
    </>
  );
}
